export type QueryKeyPart = string | number | boolean | null | undefined;
export type QueryKeyFilters = Record<string, QueryKeyPart>;

export const queryKeys = {
  admin: {
    root: ["admin"] as const,
    session: () => ["admin", "session"] as const,
    profile: () => ["admin", "profile"] as const,
    admins: (filters?: QueryKeyFilters) =>
      ["admin", "admins", filters ?? {}] as const,
    adminById: (adminId: string) => ["admin", "admins", adminId] as const,
    themes: (filters?: QueryKeyFilters) =>
      ["admin", "themes", filters ?? {}] as const,
    themeById: (themeId: string) => ["admin", "themes", themeId] as const,
    sellers: (filters?: QueryKeyFilters) =>
      ["admin", "sellers", filters ?? {}] as const,
    sellerById: (sellerId: string) => ["admin", "sellers", sellerId] as const,
    sellerOptions: () => ["admin", "seller-options"] as const,
    stores: (filters?: QueryKeyFilters) =>
      ["admin", "stores", filters ?? {}] as const,
    storeOptions: () => ["admin", "stores", "options"] as const,
    storeById: (storeId: string) => ["admin", "stores", storeId] as const,
    storeStats: (storeId: string) =>
      ["admin", "stores", storeId, "stats"] as const,
    subscriptions: (filters?: QueryKeyFilters) =>
      ["admin", "subscriptions", filters ?? {}] as const,
    subscriptionById: (subscriptionId: string) =>
      ["admin", "subscriptions", subscriptionId] as const,
    storeSubscriptions: (filters?: QueryKeyFilters) =>
      ["admin", "stores", "subscriptions", filters ?? {}] as const,
    walletTopUpRequests: (filters?: QueryKeyFilters) =>
      ["admin", "stores", "wallet-top-up-requests", filters ?? {}] as const,
    storeSubscriptionStatus: (storeId: string) =>
      ["admin", "stores", storeId, "subscription-status"] as const,
    storeActivationQuote: (storeId: string, planId?: string) =>
      [
        "admin",
        "stores",
        storeId,
        "activation-quote",
        ...(planId ? [planId] : []),
      ] as const,
    storeSubscriptionAssignments: (storeId: string) =>
      ["admin", "stores", storeId, "subscription-assignments"] as const,
    storeSubscriptionPayments: (storeId: string, filters?: QueryKeyFilters) =>
      [
        "admin",
        "stores",
        storeId,
        "subscription-payments",
        filters ?? {},
      ] as const,
    storeSubscriptionCurrentSchedule: (storeId: string) =>
      ["admin", "stores", storeId, "subscription-schedule", "current"] as const,
    storeSubscriptionLedger: (storeId: string) =>
      ["admin", "stores", storeId, "subscription-schedule", "ledger"] as const,
    storeSubscriptionPaymentProofUrl: (storeId: string, paymentId: string) =>
      [
        "admin",
        "stores",
        storeId,
        "subscription-payments",
        paymentId,
        "proof-url",
      ] as const,
    storeOrders: (storeId: string, filters?: QueryKeyFilters) =>
      ["admin", "stores", storeId, "orders", filters ?? {}] as const,
    storeOrderById: (storeId: string, orderId: string) =>
      ["admin", "stores", storeId, "orders", orderId] as const,
    storeOrderProof: (storeId: string, orderId: string) =>
      ["admin", "stores", storeId, "orders", orderId, "proof"] as const,
    storeOrderPaymentTransaction: (storeId: string, orderId: string) =>
      [
        "admin",
        "stores",
        storeId,
        "orders",
        orderId,
        "payment-transaction",
      ] as const,
    appConfigs: () => ["admin", "app-configs"] as const,
    areas: (filters?: QueryKeyFilters) =>
      ["admin", "areas", filters ?? {}] as const,
    // Used ONLY by the area-suggestions duplicate hint: all areas for one
    // governorate, walked across every page. See useAreasForDuplicateHintQuery.
    areasAllForGovernorate: (governorateId: string) =>
      ["admin", "areas-all-for-governorate", governorateId] as const,
    areaGovernorates: () => ["admin", "area-governorates"] as const,
    areaSuggestions: (filters?: QueryKeyFilters) =>
      ["admin", "area-suggestions", filters ?? {}] as const,
    appConfigAudit: (key: string) =>
      ["admin", "app-configs", key, "audit"] as const,
    featureFlags: () => ["admin", "feature-flags"] as const,
    featureFlagAudit: (id: string) =>
      ["admin", "feature-flags", id, "audit"] as const,
    integrationLogs: (filters?: QueryKeyFilters) =>
      ["admin", "integration-logs", filters ?? {}] as const,
    integrationLogById: (logId: string) =>
      ["admin", "integration-logs", logId] as const,
    paidServicesUsage: (months: number) =>
      ["admin", "paid-services", "usage", months] as const,
  },
} as const;
